import { useCallback, useEffect, useRef, useState } from "react";
import { getProgress, saveProgress } from "../services/ProgressService";

/**
 * Evidence collection for document content (modules / lessons / PDFs).
 *
 * The server decides completion (see backend utils/completion.ts); this hook
 * only reports what actually happened:
 *
 *   - FOREGROUND dwell. A tick is banked only while the tab is visible AND the
 *     window has focus, so a module left open in a background tab accumulates
 *     nothing. This is the difference between "attended" and "read it".
 *   - Pages/slides actually rendered, via `reportPage`.
 *
 * Beats are flushed on an interval and again on hide/unmount, so closing the
 * viewer never loses the seconds since the last flush. Everything is
 * best-effort: a failed save can never break the viewer.
 */

const TICK_MS = 1000;
const FLUSH_EVERY_TICKS = 15; // send at most one request per 15 foreground seconds

export const useContentProgress = ({ contentType, contentId, enabled = true }) => {
  const [status, setStatus] = useState(null);

  // Dwell banked locally since the last successful flush.
  const pendingDwellRef = useRef(0);
  const tickCountRef = useRef(0);
  // Pages seen since the last flush. A Set, not a single value: a learner can
  // flip through ten slides inside one beat window and all ten must be credited.
  const pendingPagesRef = useRef(new Set());
  const pageCountRef = useRef(0);
  const intervalRef = useRef(null);
  // Identity of the content the pending evidence belongs to, so a mid-flight
  // switch to another module can never credit the wrong one.
  const targetRef = useRef({ contentType, contentId });

  const active = enabled && !!contentType && !!contentId;

  const flush = useCallback(
    async (force = false) => {
      const { contentType: ct, contentId: ci } = targetRef.current;
      if (!ct || !ci) return;
      const dwell = pendingDwellRef.current;
      const pages = [...pendingPagesRef.current];
      if (!force && dwell <= 0 && pages.length === 0) return;

      pendingDwellRef.current = 0;
      pendingPagesRef.current = new Set();

      const res = await saveProgress({
        contentType: ct,
        contentId: ci,
        dwellDelta: dwell,
        pages,
        pageCount: pageCountRef.current || undefined,
      });
      if (res) setStatus(res);
    },
    []
  );

  // Load whatever evidence already exists, so the viewer can show what's left.
  useEffect(() => {
    targetRef.current = { contentType, contentId };
    pendingDwellRef.current = 0;
    pendingPagesRef.current = new Set();
    pageCountRef.current = 0;
    tickCountRef.current = 0;
    setStatus(null);
    if (!active) return;

    let cancelled = false;
    (async () => {
      const p = await getProgress(contentType, contentId);
      if (!cancelled && p) setStatus(p);
    })();
    return () => {
      cancelled = true;
    };
  }, [contentType, contentId, active]);

  // Foreground-gated dwell accumulation.
  useEffect(() => {
    if (!active) return undefined;

    const isForeground = () =>
      document.visibilityState === "visible" && document.hasFocus();

    intervalRef.current = setInterval(() => {
      if (!isForeground()) return;
      pendingDwellRef.current += TICK_MS / 1000;
      tickCountRef.current += 1;
      if (tickCountRef.current % FLUSH_EVERY_TICKS === 0) flush();
    }, TICK_MS);

    // Don't lose the tail: flush whatever is banked whenever we lose the user.
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    const onBlur = () => flush();
    const onUnload = () => flush();

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    window.addEventListener("beforeunload", onUnload);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("beforeunload", onUnload);
      flush(); // covers unmount and switching to different content
    };
  }, [active, flush, contentType, contentId]);

  /** Called by a viewer whenever a page/slide is rendered. 1-based. */
  const reportPage = useCallback((page, pageCount) => {
    if (pageCount > 0) pageCountRef.current = pageCount;
    if (page > 0) pendingPagesRef.current.add(page);
  }, []);

  return { status, reportPage, flush };
};

export default useContentProgress;
